import { PortalErrorNotice } from '@/components/common/portal-error-notice';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { recordFeedbackBreadcrumb } from '@/lib/feedback-diagnostics';

export function RouteErrorBoundary(): JSX.Element {
  const { t } = useTranslation();
  const error = useRouteError();

  useEffect(() => {
    recordFeedbackBreadcrumb({
      type: 'ERROR_BOUNDARY',
      actionId: 'route.error-boundary',
      actionLabelKey: 'diagnostics.actions.errorBoundary',
      entityType: null,
      entityId: null,
      result: 'FAILED',
      errorTrackingId: null,
    });
  }, []);

  if (window.location.pathname.startsWith('/portal'))
    return <PortalErrorNotice error={error} kind="server" retry={() => window.location.reload()} />;

  const detail = isRouteErrorResponse(error)
    ? `${String(error.status)} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : null;

  return (
    <div className="flex min-h-full flex-col items-start justify-center gap-4 p-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold tracking-tight">{t('errorPage.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('errorPage.description')}</p>
        {detail === null ? null : (
          <p className="font-mono text-xs text-muted-foreground">{detail}</p>
        )}
      </div>
      <Button variant="outline" onClick={() => window.location.reload()}>
        {t('errorPage.reload')}
      </Button>
    </div>
  );
}
